/**
 * @file Contact CTA.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Contact CTA Component.
 */
const contactCTA = async () => {
	const contact = document.querySelector('.v-contact-cta');

	if (!contact) {
		return;
	}

	// const fields       = contact.querySelectorAll( 'input, select, textarea, button' );
	const formFields   = contact.querySelectorAll('.v-form__field:nth-child(2) ~ .v-form__field');
	const formMessage  = contact.querySelector('.wpcf7-response-output');
	const contactMask  = contact.querySelector('.v-contact-cta__form');
	const contactForm  = contact.querySelector('form');
	const contactInner = contact.querySelector('.v-contact-cta__form-inner');
	let isOpen         = false;

	const mainSwitcher = contact.querySelector('.v-form__field:first-child select:not(.v-trigger-ignore)');
	const nextSwitcher = contact.querySelector('.v-form__field:nth-child(2) select');
	const conditionalB = contact.querySelectorAll('[data-id^="business-"]');
	const conditionalP = contact.querySelectorAll('[data-id^="personal-"]');
	const conditionalC = contact.querySelectorAll('[data-id^="customer-"]');
	const stateSelect  = contact.querySelector('select[name="state"]');

	/**
	 * Reveals / expands the form.
	 */
	const revealForm = () => {
		isOpen = true;

		// remove d:none so gsap can know height
		[...formFields].forEach((field) => {
			field.style.display = 'inline-block';
		});

		gsap.to(contactMask, {
			duration:   0.5,
			height:     contactInner.getBoundingClientRect().height,
			onComplete: () => {
				contactMask.classList.add('open');
				contactMask.removeAttribute('style');
			},
		});
	};

	/**
	 * Shrinks / hides the form.
	 */
	const hideForm = () => {
		isOpen = false;

		// add d:none so tabbing sequence is correct
		[...formFields].forEach((field) => {
			field.style.display = 'none';
		});

		gsap.to(contactMask, {
			duration:   0.5,
			height:     matchMedia('(max-width: 550px)').matches ? 89.6 : 99.6,
			onComplete: () => {
				contactMask.classList.remove('open');
				contactMask.removeAttribute('style');
			},
		});
	};

	/**
	 * Update UI depending on what US State is selected.
	 *
	 * @param e
	 */
	const stateChange = (e) => {
		const { target } = e;

		// use cf7 response div to display warning
		if (target.value.toLowerCase() === 'other') {
			console.log('show modal');
			formMessage.textContent = 'We apologize but it looks like you are outside of our current service area. Please visit our Locations page to see our list of branches in Indiana, Kentucky, Maryland, Ohio, Pennsylvania, Tennessee, and West Virginia.';
			formMessage.style.display = 'block';
			formMessage.classList.add('show');
			contactForm.classList.add('invalid');
		}

		// remove warning if other state selected
		if (target.value.toLowerCase() !== 'other') {
			if (formMessage.classList.contains('show')) {
				formMessage.textContent = '';
				contactForm.classList.remove('invalid');
				contactForm.classList.add('init');
				formMessage.removeAttribute('style');
				formMessage.classList.remove('show');
			}

			if (target.parentElement.nextElementSibling && target.parentElement.nextElementSibling.classList.contains('wpcf7-not-valid-tip')) {
				target.parentElement.nextElementSibling.style.display = 'none';
			}
		}
	};

	if (mainSwitcher) {
		mainSwitcher.addEventListener('change', () => {
			const val = mainSwitcher.value;
			if (val === 'Business Products & Services') {
				conditionalB.forEach((g) => g.classList.add('open'));
				conditionalP.forEach((g) => g.classList.remove('open'));
				conditionalC.forEach((g) => g.classList.remove('open'));
			} else if (val === 'Personal Products & Services') {
				conditionalP.forEach((g) => g.classList.add('open'));
				conditionalB.forEach((g) => g.classList.remove('open'));
				conditionalC.forEach((g) => g.classList.remove('open'));
			} else if (val === 'Customer Service') {
				conditionalC.forEach((g) => g.classList.add('open'));
				conditionalB.forEach((g) => g.classList.remove('open'));
				conditionalP.forEach((g) => g.classList.remove('open'));
			} else {
				conditionalP.forEach((g) => g.classList.remove('open'));
				conditionalB.forEach((g) => g.classList.remove('open'));
				conditionalC.forEach((g) => g.classList.remove('open'));
			}

			if (val && !isOpen) {
				revealForm();
			} else if (!val && isOpen) {
				if (!(nextSwitcher && nextSwitcher.value)) {
					hideForm();
				}
			}
		});

		if (nextSwitcher) {
			nextSwitcher.addEventListener('change', () => {
				if (nextSwitcher.value && !isOpen) {
					revealForm();
				} else if (!nextSwitcher.value && isOpen) {
					if (!mainSwitcher.value) {
						hideForm();
					}
				}
			});
		}

		const email  = contact.querySelector('[type="email"]');
		const helper = contact.querySelector('.v-contact-cta__helper');
		if (email) {
			email.addEventListener('change', () => {
				if (email.value && !mainSwitcher.value) {
					helper.classList.add('show');
				} else {
					helper.classList.remove('show');
				}
			});
		}

		mainSwitcher.addEventListener('focus', () => {
			helper.classList.remove('show');
		});

		mainSwitcher.addEventListener('blur', () => {
			if (email.value && !mainSwitcher.value) {
				helper.classList.add('show');
			} else {
				helper.classList.remove('show');
			}
		});
	} else {
		const firstTextInput  = contact.querySelector('.v-form__field:first-child input[type="text"]');

		if (firstTextInput) {
			const secondTextInput = contact.querySelector('.v-form__field:nth-child(2) input[type="text"]');
			contact.setAttribute('data-expanded', 'false');

			firstTextInput.addEventListener('input', () => {
				if (firstTextInput.value.length && contact.getAttribute('data-expanded') === 'false') {
					revealForm();
					contact.setAttribute('data-expanded', 'true');
				}

				if (!firstTextInput.value.length
					&& !secondTextInput.value.length
					&& contact.getAttribute('data-expanded') === 'true'
				) {
					hideForm();
					contact.setAttribute('data-expanded', 'false');
				}
			});
		}
	}

	let submittedType = '';
	const form        = contact.querySelector('form');

	form.addEventListener('submit', () => {
		submittedType = mainSwitcher.value;
	});

	if (stateSelect) {
		stateSelect.addEventListener('change', stateChange);
	}

	document.addEventListener('wpcf7mailsent', (e) => {
		if (e.detail.contactFormId !== 5) {
			return;
		}
		setTimeout(() => {
			mainSwitcher.value = submittedType;
		}, 0);
	}, false);

	const peaks = document.querySelector('.v-contact-cta__peaks');
	if (!peaks) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);

	gsap.to('.v-contact-cta__peaks', {
		'--valley':    '0vw',
		ease:          'sine.out',
		scrollTrigger: {
			trigger: '.v-contact-cta',
			start:   'top 85%',
			end:     'top 50%',
			scrub:   true,
		},
	});
};

export default contactCTA;
